import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { useGridApiRef } from "@mui/x-data-grid";
import { Tag } from "primereact/tag";
import { Col, Row } from "react-bootstrap";
import moment from "moment";
import Langs, { Localize } from "../../app/lang/langs";
import { getEmploymentPromises } from "../../components/employmentPromises/employmentPromisesSlice";
import ImageButton from "../../components/buttons/ImageButton";
import addIcon from "../../assets/images/add.png";
import addIconFocus from "../../assets/images/addFocus.png";
import EmploymentPromisesAddModal from "./EmploymentPromisesAddModal";
import EmploymentPromisesEditModal from "./EmploymentPromisesEditModal";
import styles from "./CollabsView.module.css";
import { singleSelectFilterOperator } from "./collabsTableUtils";

export default function EmploymentPromisesView() {
  const dispatch = useDispatch();
  const mounted = useRef();
  const apiRef = useGridApiRef();

  const list = useSelector((state) => state.employmentPromises.list);
  const isMobile = useSelector((state) => state.settings.isMobile);
  const lang = useSelector((state) => state.settings.lang);
  const dataGridLocale = lang === "fr" ? frFR : enUS;

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [globalSearch, setGlobalSearch] = useState("");
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [onViewPromise, setOnViewPromise] = useState(null);
  const [onViewVisibile, setOnViewVisible] = useState(false);
  const [onAddViewVisible, setOnAddViewVisible] = useState(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      dispatch(getEmploymentPromises());
    }
  });

  const getFormattedDate = (date) => {
    if (!date) return null;
    return moment(date).format("DD/MM/YYYY");
  };

  const columns = useMemo(() => [
    { field: "id", headerName: Localize("ID"), minWidth: 112, sortable: true, filterable: true },
    { field: "company_name", headerName: Localize("Company"), minWidth: 128, sortable: true, filterable: true },
    { field: "family_name", headerName: Localize("Family Name"), minWidth: 128, sortable: true, filterable: true },
    { field: "name", headerName: Localize("Name"), minWidth: 144, sortable: true, filterable: true },
    { field: "email", headerName: Localize("EMAIL"), minWidth: 144, sortable: true, filterable: true },
    {
      field: "progression", headerName: Localize("Progression"), minWidth: 160, sortable: true, filterable: true,
      filterOperators: [singleSelectFilterOperator([
        { value: "Attente d'envoi", label: "Attente d'envoi" },
        { value: "Envoyé", label: "Envoyé" },
        { value: "Reçu", label: "Reçu" },
      ])],
      renderCell: (params) => {
        const status = params.value;
        let severity;
        switch (status) {
          case "Attente d'envoi": severity = "warning"; break;
          case "Envoyé": severity = "info"; break;
          case "Reçu": severity = "success"; break;
          default: severity = null;
        }
        return <Tag value={status} severity={severity} />;
      },
    },
    {
      field: "status", headerName: Localize("STATUS"), minWidth: 128, sortable: true, filterable: true,
      filterOperators: [singleSelectFilterOperator([
        { value: "OK", label: "OK" },
        { value: "KO", label: "KO" },
      ])],
      renderCell: (params) => (
        <span className={`badge ${params.value === "OK" ? "bg-success" : "bg-danger"}`}>
          {params.value}
        </span>
      ),
    },
    {
      field: "request_date", headerName: Localize("REQUEST_DATE"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => params.value ? <label>{getFormattedDate(params.value)}</label> : null,
    },
    {
      field: "sent_date", headerName: Localize("SENT_DATE"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => params.value ? <label>{getFormattedDate(params.value)}</label> : null,
    },
    {
      field: "start_date", headerName: Localize("START_DATE"), minWidth: 144,
      type: "date", sortable: true, filterable: true,
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => params.value ? <label>{getFormattedDate(params.value)}</label> : null,
    },
  ], []);

  const onRowDoubleClick = (params) => {
    setOnViewVisible(true);
    setOnViewPromise(params.row);
  };

  let scrollHeight = "calc(100vh - 160px)";
  switch (isMobile) {
    case 2: scrollHeight = "calc(100vh - 220px)"; break;
    case 3: scrollHeight = "calc(100vh - 160px)"; break;
    default: break;
  }

  return (
    <div className={styles.collabsCard}>
      <Row className="p-2 mb-1">
        <Col md={6} className="d-flex align-items-center">
          <input
            type="text"
            className="form-control"
            placeholder={Localize("Search") + "..."}
            value={globalSearch}
            onChange={(e) => {
              const val = e.target.value;
              setGlobalSearch(val);
              setFilterModel((prev) => ({
                ...prev,
                quickFilterValues: val ? val.split(" ") : [],
              }));
            }}
            style={{ maxWidth: 350 }}
          />
        </Col>
        <Col md={6} className="d-flex justify-content-end">
          <ImageButton
            image={addIcon}
            imageFocus={addIconFocus}
            onClick={() => setOnAddViewVisible(true)}
            active={true}
            width={32}
          />
        </Col>
      </Row>
      <div style={{ height: scrollHeight, width: "100%" }}>
        <DataGrid
          localeText={dataGridLocale.components.MuiDataGrid.defaultProps.localeText}
          apiRef={apiRef}
          rows={list || []}
          columns={columns}
          getRowId={(row) => row.id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          onRowDoubleClick={onRowDoubleClick}
          filterModel={filterModel}
          onFilterModelChange={(newModel) => {
            setFilterModel((prev) => ({
              ...newModel,
              quickFilterValues: prev.quickFilterValues,
            }));
          }}
          rowHeight={52}
          disableRowSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e0e0e0",
              minHeight: 52,
            },
            "& .MuiTablePagination-root": {
              width: "100%",
              overflow: "hidden",
            },
            "& .MuiTablePagination-toolbar": {
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: 52,
              px: 2,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              margin: 0,
            },
          }}
        />
      </div>
      <EmploymentPromisesAddModal
        visible={onAddViewVisible}
        setVisible={setOnAddViewVisible}
      />
      <EmploymentPromisesEditModal
        visible={onViewVisibile}
        setVisible={setOnViewVisible}
        onViewItem={onViewPromise}
        setOnViewItem={setOnViewPromise}
      />
    </div>
  );
}
